<template>
    <div class="tw:space-y-4">
        <div class="tw:mb-4 tw:px-6">
            <h2 class="tw:text-sm tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wide tw:my-1">Lesiones</h2>
            <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:my-1">Historial de lesiones del deportista</p>
        </div>

        <div class="tw:flex tw:justify-end tw:px-6">
            <Button
                @click="$emit('add-lesion')"
                class="tw:text-xs tw:sm:text-sm"
            >
                Añadir lesión
            </Button>
        </div>

        <div class="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-3 tw:px-6 tw:mb-3">
            <BaseInputText
                label="Buscar"
                :value="local.search"
                name="lesiones-search"
                @input="onSearchInput"
            />

            <BaseMultiselect
                name="lesiones-filter"
                label="Filtrar por"
                :options="filterOptions"
                value-key="id"
                label-key="name"
                :value="local.filter"
                @input="val => local.filter = (val ?? 'orientacion_diagnostica')"
            />
        </div>

        <SimpleTable
            :flat="true"
            id="lesiones"
            :columns="columns"
            :data="lesiones"
            :skeleton="!fetched || loading"
            :skeleton-rows="3"
            empty-text="No hay lesiones registradas"
            :default-col-width="defaultColWidth"
        >
            <!-- Skeleton headers -->
            <template #skeleton-header-fecha_baja>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-16"></div>
            </template>
            <template #skeleton-header-fecha_alta>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-16"></div>
            </template>
            <template #skeleton-header-orientacion_diagnostica>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-32"></div>
            </template>
            <template #skeleton-header-opciones>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-14"></div>
            </template>

            <!-- Skeleton rows -->
            <template #skeleton-fecha_baja>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-36"></div>
            </template>
            <template #skeleton-fecha_alta>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-36"></div>
            </template>
            <template #skeleton-orientacion_diagnostica>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-64"></div>
            </template>
            <template #skeleton-opciones>
                <div class="tw:h-8 tw:w-16 tw:bg-gray-200 tw:rounded"></div>
            </template>

            <template #cell-orientacion_diagnostica="{ row }">
                <span v-html="row.orientacion_diagnostica"></span>
            </template>
            <template #cell-opciones="{ row }">
                <Button
                    @click="$emit('delete-lesion', row)"
                    variant="danger"
                    class="tw:text-xs tw:sm:text-sm"
                >
                    Borrar
                </Button>
            </template>
        </SimpleTable>
    </div>
</template>

<script setup>
    import { reactive, watch, onUnmounted } from 'vue'
    import Button from '@/modules/admin/ui/Button.vue'
    import SimpleTable from '@/modules/admin/ui/table/SimpleTable.vue'
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue'
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue'

    const props = defineProps({
        lesiones: {
            type: Array,
            default: () => []
        },
        fetched: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        lesionesSearch: {
            type: String,
            default: ''
        },
        lesionesFilter: {
            type: String,
            default: 'orientacion_diagnostica'
        },
        defaultColWidth: {
            type: String,
            default: '140px'
        }
    })

    const emit = defineEmits(['add-lesion', 'delete-lesion', 'update:lesionesSearch', 'update:lesionesFilter'])

    const local = reactive({
        search: props.lesionesSearch,
        filter: props.lesionesFilter
    })

    let searchTimer = null

    function onSearchInput(val) {
        local.search = val
        clearTimeout(searchTimer)
        searchTimer = setTimeout(() => {
            emit('update:lesionesSearch', local.search)
        }, 500)
    }

    watch(() => props.lesionesSearch, val => { local.search = val })
    watch(() => props.lesionesFilter, val => { local.filter = val })

    watch(() => local.filter, val => {
        emit('update:lesionesFilter', val)
    })

    onUnmounted(() => {
        clearTimeout(searchTimer)
    })

    const filterOptions = [
        { id: 'orientacion_diagnostica', name: 'Orientación diagnóstica' },
        { id: 'fecha_baja', name: 'Fecha baja' },
        { id: 'fecha_alta', name: 'Fecha alta' },
    ]

    const columns = [
        { key: 'fecha_baja', label: 'Fecha baja' },
        { key: 'fecha_alta', label: 'Fecha alta' },
        { key: 'orientacion_diagnostica', label: 'Orientación diagnóstica' },
        { key: 'opciones', label: 'Acciones' }
    ]
</script>
